import React, { useState, useMemo, useRef } from 'react';
import { Modal, Button, Form, Col, Alert, Spinner, Badge } from 'react-bootstrap';
import { searchPartsTech, getTypeahead, addToCart, getCart } from '../../../services/partsTech';
import PartsTechDetailModal from './PartsTechDetailModal';
import PartsTechCartModal from './PartsTechCartModal';
import PartsTechStoreStock from './PartsTechStoreStock';

const AVAIL_OPTIONS = [
    { value: 'in-stock', label: 'In Stock at Home Store' },
    { value: 'network',  label: 'In Network' },
    { value: 'all',      label: 'All' },
];

export default function PartsTechModal({ show, onHide, vehicle, onSelectPart, addPart, markup = 1.95, markupType = 1 }) {
    const [query, setQuery]                 = useState({ keyword: '', partNumber: '' });
    const [distributors, setDistributors]   = useState([]);
    const [resolvedVehicle, setResolvedVehicle] = useState(null);
    const [activeSupplier, setActiveSupplier]   = useState(null);
    const [loading, setLoading]             = useState(false);
    const [error, setError]                 = useState('');
    const [sort, setSort]                   = useState({ key: 'availability', dir: 'desc' });
    const [detailPart, setDetailPart]       = useState(null);
    const [suggestions, setSuggestions]     = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showCart, setShowCart]           = useState(false);
    const [cartCount, setCartCount]         = useState(0);
    const [addingToCart, setAddingToCart]   = useState(null);
    const [filters, setFilters]             = useState({ availability: 'in-stock', brands: new Set(), positions: new Set() });
    const debounceRef = useRef(null);

    const vehicleLabel = vehicle?.Year && vehicle?.Make && vehicle?.Model
        ? `${vehicle.Year} ${vehicle.Make} ${vehicle.Model}`
        : null;

    // ── Typeahead ────────────────────────────────────────────────────────────
    function handleKeywordChange(value) {
        setQuery({ ...query, keyword: value });
        setShowSuggestions(false);
        clearTimeout(debounceRef.current);
        if (value.length < 2) { setSuggestions([]); return; }
        debounceRef.current = setTimeout(async () => {
            try {
                const results = await getTypeahead(value);
                setSuggestions(results || []);
                setShowSuggestions(true);
            } catch (_) {}
        }, 300);
    }

    function selectSuggestion(name) {
        setQuery({ ...query, keyword: name });
        setSuggestions([]);
        setShowSuggestions(false);
    }

    // ── Cart ─────────────────────────────────────────────────────────────────
    async function refreshCartCount() {
        try {
            const c = await getCart();
            const count = (c?.orders || []).reduce((sum, o) => sum + o.items.length, 0);
            setCartCount(count);
        } catch (_) {}
    }

    async function handleAddToCart(part, e) {
        e.stopPropagation();
        if (!part.partNumberId || !part.lineCardId || !part.accountId || !part.storeId) {
            alert('Missing cart info for this part. Try searching again.');
            return;
        }
        setAddingToCart(part.partNumber);
        try {
            await addToCart({
                partNumberId: part.partNumberId,
                partNumber:   part.partNumber,
                lineCardId:   part.lineCardId,
                accountId:    part.accountId,
                storeId:      part.storeId,
                quantity:     1,
                partName:     part.description,
                vehicleId:    resolvedVehicle?.id || undefined,
                vin:          vehicle?.VIN        || undefined,
            });
            await refreshCartCount();
        } catch (err) {
            console.error('addToCart error', err);
            alert(err?.response?.data?.error || 'Failed to add to cart');
        } finally {
            setAddingToCart(null);
        }
    }

    // ── Search ───────────────────────────────────────────────────────────────
    async function handleSearch(e) {
        e.preventDefault();
        setError('');
        setDistributors([]);
        setResolvedVehicle(null);
        setActiveSupplier(null);
        setFilters({ availability: 'in-stock', brands: new Set(), positions: new Set() });

        const hasVIN   = !!vehicle?.VIN;
        const hasPlate = !!(vehicle?.LicenseNumber && vehicle?.State);
        const hasYMM   = !!(vehicle?.Year && vehicle?.Make && vehicle?.Model);

        if (query.keyword && !hasVIN && !hasPlate && !hasYMM) {
            setError('Vehicle information is required. Add a Year, Make, and Model to the vehicle first.');
            return;
        }

        setLoading(true);
        try {
            const data = await searchPartsTech({
                partNumber: query.partNumber || undefined,
                keyword:    query.keyword    || undefined,
                vin:        vehicle?.VIN     || undefined,
                plate:      hasPlate ? { plate: vehicle.LicenseNumber, state: vehicle.State } : undefined,
                year:       vehicle?.Year    || undefined,
                make:       vehicle?.Make    || undefined,
                model:      vehicle?.Model   || undefined,
                engine:     vehicle?.Engine  || undefined,
            });
            const dists = data?.distributors || [];
            setDistributors(dists);
            setResolvedVehicle(data?.resolvedVehicle || null);
            if (dists.length > 0) setActiveSupplier(dists[0].supplier);
        } catch (err) {
            setError(err?.response?.data?.error || 'PartsTech search failed. Check backend logs.');
        } finally {
            setLoading(false);
        }
    }

    function selectPart(part) {
        onSelectPart({ partNumber: part.partNumber, description: part.description, cost: part.cost, list: part.list, distributor: part.supplier });
        onHide();
    }

    // ── Derived data ─────────────────────────────────────────────────────────
    const activeDistributor = distributors.find(d => d.supplier === activeSupplier);
    const totalResults      = distributors.reduce((sum, d) => sum + d.products.length, 0);

    // Build sidebar filter options from current distributor's full product list
    const filterOptions = useMemo(() => {
        const products  = activeDistributor?.products || [];
        const brands    = {};
        const positions = {};
        products.forEach(p => {
            if (p.brand) brands[p.brand] = (brands[p.brand] || 0) + 1;
            const posAttr = p.attributes?.find(a => a.name === 'Position');
            if (posAttr) (Array.isArray(posAttr.values) ? posAttr.values : [posAttr.values])
                .filter(Boolean).forEach(v => { positions[v] = (positions[v] || 0) + 1; });
        });
        return { brands, positions };
    }, [activeDistributor]);

    const filteredProducts = useMemo(() => {
        let products = activeDistributor?.products || [];

        if (filters.availability === 'in-stock')
            products = products.filter(p => p.availability > 0);
        else if (filters.availability === 'network')
            products = products.filter(p => p.stores?.some(s => s.quantity > 0) || p.availability > 0);

        if (filters.brands.size > 0)
            products = products.filter(p => filters.brands.has(p.brand));

        if (filters.positions.size > 0)
            products = products.filter(p => {
                const pos = p.attributes?.find(a => a.name === 'Position');
                if (!pos) return false;
                const vals = Array.isArray(pos.values) ? pos.values : [pos.values];
                return vals.some(v => filters.positions.has(v));
            });

        return [...products].sort((a, b) => {
            const av = a[sort.key] ?? '';
            const bv = b[sort.key] ?? '';
            if (typeof av === 'number') return sort.dir === 'asc' ? av - bv : bv - av;
            return sort.dir === 'asc'
                ? String(av).localeCompare(String(bv))
                : String(bv).localeCompare(String(av));
        });
    }, [activeDistributor, filters, sort]);

    function toggleBrand(brand) {
        setFilters(f => {
            const next = new Set(f.brands);
            next.has(brand) ? next.delete(brand) : next.add(brand);
            return { ...f, brands: next };
        });
    }

    function togglePosition(pos) {
        setFilters(f => {
            const next = new Set(f.positions);
            next.has(pos) ? next.delete(pos) : next.add(pos);
            return { ...f, positions: next };
        });
    }

    const hasActiveFilters = filters.availability !== 'all' || filters.brands.size > 0 || filters.positions.size > 0;

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <>
        <PartsTechDetailModal
            part={detailPart}
            onHide={() => setDetailPart(null)}
            onSelectPart={(p) => { onSelectPart(p); setDetailPart(null); }}
            resolvedVehicle={resolvedVehicle}
            vehicleVin={vehicle?.VIN}
        />
        <PartsTechCartModal
            show={showCart}
            onHide={() => setShowCart(false)}
            onOrderPlaced={refreshCartCount}
            addPart={addPart}
            markup={markup}
            markupType={markupType}
        />
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                    <span>
                        PartsTech Catalog Search
                        {vehicleLabel && <small className="text-muted ml-2" style={{ fontSize: '0.75em' }}>— {vehicleLabel}</small>}
                    </span>
                    <Button size="sm" variant="outline-dark" style={{ marginLeft: 'auto', marginRight: 28 }}
                        onClick={() => { refreshCartCount(); setShowCart(true); }}>
                        🛒 Cart{cartCount > 0 ? ` (${cartCount})` : ''}
                    </Button>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ paddingTop: 12 }}>
                {/* Search bar */}
                <Form onSubmit={handleSearch}>
                    <Form.Row className="align-items-end">
                        <Form.Group as={Col} xs={4}>
                            <Form.Label>Part Number</Form.Label>
                            <Form.Control
                                placeholder="e.g. FL500S"
                                value={query.partNumber}
                                onChange={e => setQuery({ ...query, partNumber: e.target.value.toUpperCase() })}
                            />
                        </Form.Group>
                        <Form.Group as={Col} xs={5} style={{ position: 'relative' }}>
                            <Form.Label>Keyword / Description</Form.Label>
                            <Form.Control
                                placeholder="e.g. oil filter, brake pad..."
                                value={query.keyword}
                                onChange={e => handleKeywordChange(e.target.value)}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                                autoComplete="off"
                            />
                            {showSuggestions && suggestions.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
                                    background: '#fff', border: '1px solid #ced4da',
                                    borderRadius: '0 0 4px 4px', maxHeight: 220, overflowY: 'auto',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                }}>
                                    {suggestions.map((s, i) => (
                                        <div key={i} onMouseDown={() => selectSuggestion(s.name)}
                                            style={{ padding: '6px 12px', cursor: 'pointer', fontWeight: s.suggested ? 600 : 400, borderBottom: '1px solid #f0f0f0' }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                                            onMouseLeave={e => e.currentTarget.style.background = ''}>
                                            {s.name}
                                            {s.isPartNumber && s.brand && <small className="text-muted ml-2">{s.brand}</small>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Form.Group>
                        <Form.Group as={Col} xs={3}>
                            <Button type="submit" variant="primary" block disabled={loading}>
                                {loading ? <Spinner animation="border" size="sm" /> : 'Search'}
                            </Button>
                        </Form.Group>
                    </Form.Row>
                </Form>

                {error && <Alert variant="danger">{error}</Alert>}

                {/* Vehicle match banner */}
                {resolvedVehicle && (
                    <Alert variant="info" style={{ padding: '5px 12px', marginBottom: 10 }}>
                        <strong>Matched: </strong>
                        {[resolvedVehicle.year, resolvedVehicle.make, resolvedVehicle.model, resolvedVehicle.subModel].filter(Boolean).join(' ')}
                        {resolvedVehicle.engine && <span className="text-muted"> — {resolvedVehicle.engine}</span>}
                        {resolvedVehicle.vin    && <span className="text-muted"> — VIN: {resolvedVehicle.vin}</span>}
                    </Alert>
                )}

                {/* Distributor tabs */}
                {distributors.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                        {distributors.map(d => (
                            <Button key={d.supplier} size="sm"
                                variant={activeSupplier === d.supplier ? 'primary' : 'outline-secondary'}
                                onClick={() => { setActiveSupplier(d.supplier); setFilters({ availability: 'in-stock', brands: new Set(), positions: new Set() }); }}>
                                {d.supplier}
                                <Badge variant={activeSupplier === d.supplier ? 'light' : 'secondary'} style={{ marginLeft: 5 }}>
                                    {d.products.length}
                                </Badge>
                            </Button>
                        ))}
                        <small className="text-muted ml-2" style={{ alignSelf: 'center' }}>
                            {totalResults} total across {distributors.length} distributor{distributors.length !== 1 ? 's' : ''}
                        </small>
                    </div>
                )}

                {/* Main content: sidebar + cards */}
                {(activeDistributor || loading) && (
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

                        {/* ── Sidebar filters ── */}
                        <div style={{ width: 200, flexShrink: 0 }}>
                            {/* Availability */}
                            <FilterSection title="Availability">
                                {AVAIL_OPTIONS.map(opt => (
                                    <Form.Check key={opt.value} type="radio" id={`avail-${opt.value}`}
                                        label={opt.label}
                                        checked={filters.availability === opt.value}
                                        onChange={() => setFilters(f => ({ ...f, availability: opt.value }))}
                                        style={{ fontSize: '0.85em' }}
                                    />
                                ))}
                            </FilterSection>

                            {/* Brand */}
                            {Object.keys(filterOptions.brands).length > 0 && (
                                <FilterSection title="Brand">
                                    {Object.entries(filterOptions.brands)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([brand, count]) => (
                                        <Form.Check key={brand} type="checkbox" id={`brand-${brand}`}
                                            label={<span>{brand} <span style={{ color: '#888', fontSize: '0.8em' }}>({count})</span></span>}
                                            checked={filters.brands.has(brand)}
                                            onChange={() => toggleBrand(brand)}
                                            style={{ fontSize: '0.85em' }}
                                        />
                                    ))}
                                </FilterSection>
                            )}

                            {/* Position */}
                            {Object.keys(filterOptions.positions).length > 0 && (
                                <FilterSection title="Position">
                                    {Object.entries(filterOptions.positions)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([pos, count]) => (
                                        <Form.Check key={pos} type="checkbox" id={`pos-${pos}`}
                                            label={<span>{pos} <span style={{ color: '#888', fontSize: '0.8em' }}>({count})</span></span>}
                                            checked={filters.positions.has(pos)}
                                            onChange={() => togglePosition(pos)}
                                            style={{ fontSize: '0.85em' }}
                                        />
                                    ))}
                                </FilterSection>
                            )}

                            {hasActiveFilters && (
                                <Button variant="link" size="sm" style={{ padding: 0, fontSize: '0.8em' }}
                                    onClick={() => setFilters({ availability: 'all', brands: new Set(), positions: new Set() })}>
                                    Clear all filters
                                </Button>
                            )}
                        </div>

                        {/* ── Product cards ── */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {loading && (
                                <div className="text-center py-4">
                                    <Spinner animation="border" /><p className="text-muted mt-2">Searching...</p>
                                </div>
                            )}

                            {!loading && filteredProducts.length === 0 && (
                                <div className="text-muted text-center mt-4">
                                    No {filters.availability === 'in-stock' ? 'in-stock ' : ''}results from {activeSupplier}.
                                    {filters.availability === 'in-stock' && activeDistributor?.products?.length > 0 && (
                                        <> <Button variant="link" size="sm" style={{ padding: 0 }}
                                            onClick={() => setFilters(f => ({ ...f, availability: 'all' }))}>
                                            Show all
                                        </Button></>
                                    )}
                                </div>
                            )}

                            <small className="text-muted d-block mb-2">
                                {!loading && filteredProducts.length > 0 && `${filteredProducts.length} result${filteredProducts.length !== 1 ? 's' : ''}`}
                            </small>

                            {filteredProducts.map((part, i) => (
                                <PartCard
                                    key={i}
                                    part={part}
                                    onDetail={() => setDetailPart(part)}
                                    onSelect={() => selectPart(part)}
                                    onAddToCart={e => handleAddToCart(part, e)}
                                    addingToCart={addingToCart === part.partNumber}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {!loading && distributors.length === 0 && !error && (
                    <p className="text-muted text-center mt-3">Enter a part number or keyword to search PartsTech.</p>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
        </>
    );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FilterSection({ title, children }) {
    return (
        <div style={{ marginBottom: 10, border: '1px solid #dee2e6', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ fontWeight: 600, fontSize: '0.78em', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#fff', background: '#495057', padding: '6px 10px' }}>
                {title}
            </div>
            <div style={{ padding: '8px 10px' }}>
                {children}
            </div>
        </div>
    );
}

function PartCard({ part, onDetail, onSelect, onAddToCart, addingToCart }) {
    const [hovered, setHovered] = useState(false);
    const thumb      = part.images?.[0]?.medium || part.images?.[0]?.preview;
    const posAttr    = part.attributes?.find(a => a.name === 'Position');
    const positions  = posAttr ? (Array.isArray(posAttr.values) ? posAttr.values : [posAttr.values]) : [];
    const warrantyAttr = part.attributes?.find(a => a.name === 'Warranty Time' || a.name === 'Warranty');
    const warranty   = warrantyAttr ? (Array.isArray(warrantyAttr.values) ? warrantyAttr.values[0] : warrantyAttr.values) : null;

    // Fitment notes from attributes (non-position, non-warranty)
    const fitmentAttrs = (part.attributes || []).filter(a =>
        a.name !== 'Position' && a.name !== 'Warranty Time' && a.name !== 'Warranty' &&
        (a.name?.toLowerCase().includes('fitment') || a.name?.toLowerCase().includes('fit'))
    );

    const homeStock    = part.availability || 0;
    const networkStock = (part.stores || []).filter(s => s.type !== 'MAIN').reduce((sum, s) => sum + (s.quantity || 0), 0);

    return (
        <div
            style={{
                display: 'flex', gap: 12, padding: 14, marginBottom: 8, borderRadius: 6,
                background: hovered ? '#f4f7ff' : '#fff',
                border: `1px solid ${hovered ? '#90aaee' : '#dee2e6'}`,
                boxShadow: hovered ? '0 2px 8px rgba(26,60,110,0.1)' : 'none',
                cursor: 'pointer', position: 'relative',
                transition: 'background 0.12s, border-color 0.12s, box-shadow 0.12s',
            }}
            onClick={onDetail}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {/* Image */}
            <div style={{ width: 96, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {thumb
                    ? <img src={thumb} alt={part.description} style={{ maxWidth: 96, maxHeight: 96, objectFit: 'contain' }} />
                    : <div style={{ width: 96, height: 96, background: '#f5f5f5', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: '0.75em' }}>No image</div>
                }
            </div>

            {/* Part info */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: 4 }}>
                    <strong style={{ fontSize: '1em' }}>{part.partNumber}</strong>
                    <span style={{ color: '#444', marginLeft: 8 }}>{part.description}</span>
                </div>
                <div style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {part.brand && (
                        <span style={{ background: '#1a3c6e', color: '#fff', padding: '1px 8px', borderRadius: 3, fontSize: '0.78em', fontWeight: 600 }}>
                            {part.brand}
                        </span>
                    )}
                    {positions.map((p, i) => (
                        <span key={i} style={{ background: '#e8f0fe', color: '#1a3c6e', padding: '1px 8px', borderRadius: 3, fontSize: '0.78em' }}>
                            {p}
                        </span>
                    ))}
                    {warranty && (
                        <span style={{ color: '#555', fontSize: '0.8em' }}>Warranty: {warranty}</span>
                    )}
                </div>
                {fitmentAttrs.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: part.notes?.length > 0 ? 6 : 0 }}>
                        {fitmentAttrs.map((a, i) => (
                            <span key={i} style={{ border: '1px solid #ccc', borderRadius: 3, padding: '1px 6px', fontSize: '0.76em', color: '#555' }}>
                                {a.name}: {Array.isArray(a.values) ? a.values.join(', ') : a.values}
                            </span>
                        ))}
                    </div>
                )}
                {part.notes?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        {part.notes.map((note, i) => (
                            <span key={i} style={{
                                background: '#fff8e1', border: '1px solid #ffe082',
                                borderRadius: 3, padding: '2px 7px',
                                fontSize: '0.76em', color: '#5d4037',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}>
                                <span style={{ fontSize: '0.9em' }}>⚠</span> {note}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Price + stock + actions */}
            <div style={{ width: 180, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <div style={{ fontSize: '1.3em', fontWeight: 700 }}>${part.cost.toFixed(2)}</div>
                {part.list > 0 && <div style={{ fontSize: '0.8em', color: '#888' }}>List: ${part.list.toFixed(2)}</div>}
                {part.coreCharge > 0 && <div style={{ fontSize: '0.78em', color: '#888' }}>+${part.coreCharge.toFixed(2)} core</div>}

                {/* Stock badge + store dropdown */}
                <PartsTechStoreStock homeQty={homeStock} stores={part.stores} />

                {networkStock > 0 && homeStock === 0 && (
                    <div style={{ fontSize: '0.78em', color: '#0c5460' }}>
                        🌐 {networkStock} in network
                    </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <Button size="sm" variant="success" onClick={e => { e.stopPropagation(); onSelect(); }}>
                        + Invoice
                    </Button>
                    <Button size="sm" variant="outline-primary" disabled={addingToCart} onClick={onAddToCart} title="Add to PartsTech cart">
                        {addingToCart ? <Spinner animation="border" size="sm" /> : '+ Cart'}
                    </Button>
                </div>
            </div>
        </div>
    );
}

